import React, { useMemo, useState } from 'react';
import { isApiError, resetPassword } from '../services/api';

function useQueryParam(name: string) {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name) || '';
  }, [name]);
}

const ResetPasswordPage: React.FC = () => {
  const tokenRaw = useQueryParam('token');
  const token = useMemo(() => {
    return tokenRaw
      .trim()
      .replace(/^"+|"+$/g, '')
      .replace(/\s+/g, '')
      .replace(/[^a-fA-F0-9]/g, '')
      .toLowerCase();
  }, [tokenRaw]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Lien invalide ou manquant.');
      return;
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword({ token: token ?? '', password });
      setMessage('Mot de passe réinitialisé avec succès. Vous pouvez maintenant vous connecter.');
    } catch (error) {
      if (isApiError(error) && !error.isNetworkError) {
        setError(error.message || 'Impossible de réinitialiser le mot de passe.');
      } else {
        setError('Erreur de connexion au serveur. Réessayez.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
      <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Définir un nouveau mot de passe</h1>
      {!token && (
        <div className="mb-4 p-3 rounded bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 text-sm">Lien invalide ou manquant.</div>
      )}
      {message && (
        <div className="mb-4 p-3 rounded bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 text-sm">{message}</div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nouveau mot de passe</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Nouveau mot de passe"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">8+ caractères, avec majuscules, minuscules et chiffres</p>
        </div>

        <div>
          <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirmer le mot de passe</label>
          <input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirmez le mot de passe"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            required
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !token}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Envoi...' : 'Réinitialiser le mot de passe'}
        </button>
      </form>

      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        <a href="/login" className="text-blue-600 dark:text-blue-400 hover:underline">Retour à la connexion</a>
      </div>
    </div>
  );
};

export default ResetPasswordPage;


